import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Button,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  Tooltip,
  TextField,
  InputAdornment,
  Drawer,
  List,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LogoutIcon from '@mui/icons-material/Logout';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SaveIcon from '@mui/icons-material/Save';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CertificateIcon from '@mui/icons-material/VerifiedUser';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import BusinessIcon from '@mui/icons-material/Business';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import FirebaseService from '../services/FirebaseService';
import ReusableAddCertificateForm from '../components/certificate/ReusableAddCertificateForm';
import ReusableAddOrganizationForm from '../components/organization/ReusableAddOrganizationForm';
import AddVerifiedPersonnel from './AddVerifiedPersonnel';

const AdminDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [filteredOrganizations, setFilteredOrganizations] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [filteredPersonnel, setFilteredPersonnel] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCertificates, setFilteredCertificates] = useState([]);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [showAddCertificate, setShowAddCertificate] = useState(false);
  const [showAddOrganization, setShowAddOrganization] = useState(false);
  const [showAddPersonnel, setShowAddPersonnel] = useState(false);
  const [editingCertificate, setEditingCertificate] = useState(null);
  const [editingOrganization, setEditingOrganization] = useState(null);
  const [editingPersonnel, setEditingPersonnel] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [certificateToDelete, setCertificateToDelete] = useState(null);
  const [organizationToDelete, setOrganizationToDelete] = useState(null);
  const [personnelToDelete, setPersonnelToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const navigate = useNavigate();

  // Check for tab query parameter on component mount
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const tabParam = queryParams.get('tab');
    
    if (tabParam === 'certificates') {
      setTabValue(1); // Set to certificates tab
    } else if (tabParam === 'organizations' || tabParam === 'orgs') {
      setTabValue(2); // Set to organizations tab
    } else if (tabParam === 'personnel') {
      setTabValue(3); // Set to personnel tab
    } else if (tabParam === 'settings') {
      setTabValue(4); // Set to settings tab
    }
  }, []);

  useEffect(() => {
    fetchCertificates();
    fetchOrganizations(); // Added fetch organizations
    fetchPersonnel(); // Added fetch personnel
  }, []);

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      const data = await FirebaseService.getAllCertificates();
      setCertificates(data);
      setFilteredCertificates(data);
      setLoading(false);
    } catch (error) {
      // Removed console.error
      setError('Failed to load certificates. Please try again later.');
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      // Use cached data if available (forceRefresh = false)
      const data = await FirebaseService.getAllAccreditedOrganizations(false);
      
      // Sort organizations by expiration date in descending order
      const sortedData = [...data].sort((a, b) => {
        // Handle N/A values
        if (a.exp_date === 'N/A' && b.exp_date === 'N/A') return 0;
        if (a.exp_date === 'N/A') return 1; // Put N/A at the end
        if (b.exp_date === 'N/A') return -1;
        
        // Parse dates for comparison
        try {
          const dateA = new Date(a.exp_date);
          const dateB = new Date(b.exp_date);
          return dateB.getTime() - dateA.getTime(); // Descending order (newest first)
        } catch (err) {
          // Removed console.error
          return 0;
        }
      });
      
      setOrganizations(sortedData);
      setFilteredOrganizations(sortedData);
      // Removed console.log
      setLoading(false);
    } catch (error) {
      // Removed console.error
      setError('Failed to load organizations. Please try again later.');
      setLoading(false);
    }
  };

  const fetchPersonnel = async (forceRefresh = false) => {
    try {
      setLoading(true);
      // Use the cache by default, or force refresh if requested
      const data = await FirebaseService.getAllVerifiedPersonnel(forceRefresh);
      
      // Sort personnel by issue_date in descending order (newest first)
      const sortedData = [...data].sort((a, b) => {
        try {
          // Make sure we have valid objects with issue_date property
          if (!a || !b) return 0;
          
          const dateA = a.issue_date ? new Date(a.issue_date) : new Date(0);
          const dateB = b.issue_date ? new Date(b.issue_date) : new Date(0);
          return dateB.getTime() - dateA.getTime(); // Descending order
        } catch (error) {
          // Removed console.error
          return 0;
        }
      });
      
      setPersonnel(sortedData);
      setFilteredPersonnel(sortedData);
      // Removed console.log
      setLoading(false);
    } catch (error) {
      // Removed console.error
      setError('Failed to load personnel. Please try again later.');
      setLoading(false);
    }
  };
  
  // Function to refresh personnel from server
  const refreshPersonnel = async () => {
    try {
      // Force refresh from server by passing true
      await fetchPersonnel(true);
      setError(''); // Clear any previous errors
    } catch (error) {
      // Removed console.error
      setError('Failed to refresh personnel. Please try again later.');
    }
  };
  
  // Function to refresh organizations from server
  const refreshOrganizations = async () => {
    try {
      setLoading(true);
      // Force refresh from server by passing true
      const data = await FirebaseService.getAllAccreditedOrganizations(true);
      
      // Sort organizations by expiration date in descending order
      const sortedData = [...data].sort((a, b) => {
        // Handle N/A values
        if (a.exp_date === 'N/A' && b.exp_date === 'N/A') return 0;
        if (a.exp_date === 'N/A') return 1; // Put N/A at the end
        if (b.exp_date === 'N/A') return -1;
        
        // Parse dates for comparison
        try {
          const dateA = new Date(a.exp_date);
          const dateB = new Date(b.exp_date);
          return dateB.getTime() - dateA.getTime(); // Descending order (newest first)
        } catch (err) {
          // Removed console.error
          return 0;
        }
      });
      
      setOrganizations(sortedData);
      setFilteredOrganizations(sortedData);
      // Removed console.log
    } catch (error) {
      // Removed console.error
      setError('Failed to refresh organizations. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const checkAuth = async () => {
    try {
      const currentUser = FirebaseService.getCurrentUser();
      
      if (!currentUser) {
        navigate('/login');
        return;
      }
      
      setUser(currentUser);
      
      // Load certificates with caching
      // Pass false to use cache if available
      const allCertificates = await FirebaseService.getAllCertificates(false);
      
      // Sort certificates by issue_date in descending order (newest first)
      const sortedCertificates = [...allCertificates].sort((a, b) => {
        // Parse dates properly handling different formats
        let dateA, dateB;
        
        try {
          // Handle various date formats (MMM D, YYYY or YYYY-MM-DD or D-MM-YYYY)
          if (a.issue_date) {
            if (a.issue_date.includes('-')) {
              // Handle YYYY-MM-DD or DD-MM-YYYY format
              const parts = a.issue_date.split('-');
              if (parts[0].length === 4) {
                // YYYY-MM-DD format
                dateA = new Date(a.issue_date);
              } else {
                // DD-MM-YYYY format (like 13-06-2023)
                dateA = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
              }
            } else {
              // Handle MMM D, YYYY format (like Dec 8, 2024)
              dateA = new Date(a.issue_date);
            }
          } else {
            dateA = new Date(0);
          }
          
          if (b.issue_date) {
            if (b.issue_date.includes('-')) {
              // Handle YYYY-MM-DD or DD-MM-YYYY format
              const parts = b.issue_date.split('-');
              if (parts[0].length === 4) {
                // YYYY-MM-DD format
                dateB = new Date(b.issue_date);
              } else {
                // DD-MM-YYYY format (like 13-06-2023)
                dateB = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
              }
            } else {
              // Handle MMM D, YYYY format (like Dec 8, 2024)
              dateB = new Date(b.issue_date);
            }
          } else {
            dateB = new Date(0);
          }
        } catch (error) {
          // Removed console.error
          return 0; // Keep original order if parsing fails
        }
        
        // Compare timestamps for sorting
        return dateB.getTime() - dateA.getTime(); // Descending order
      });
      
      setCertificates(sortedCertificates);
      setFilteredCertificates(sortedCertificates);
      
      // Removed console.log
      
    } catch (err) {
      // Removed console.error
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredCertificates(certificates);
      setFilteredOrganizations(organizations);
    } else {
      const filteredCerts = certificates.filter(cert => 
        cert.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cert.certificate_no?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCertificates(filteredCerts);
      
      const filteredOrgs = organizations.filter(org => 
        org.org_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.certi_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.program?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.country?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOrganizations(filteredOrgs);
    }
  }, [searchTerm, certificates, organizations]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    
    // Update URL to reflect current tab without navigating
    const tabNames = ['dashboard', 'certificates', 'organizations', 'personnel', 'settings'];
    const newUrl = `/admin/dashboard?tab=${tabNames[newValue]}`;
    window.history.replaceState(null, '', newUrl);
    
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleLogout = async () => {
    try {
      await FirebaseService.logout();
      navigate('/login');
    } catch (error) {
      // Removed console.error
      setError('Error logging out. Please try again.');
    }
  };

  // Handle editing a certificate
  const handleEditCertificate = (certificate) => {
    setEditingCertificate(certificate);
    setShowAddCertificate(true);
  };

  // Open delete confirmation dialog
  const handleDeleteCertificate = (certificate) => {
    setCertificateToDelete(certificate);
    setDeleteConfirmText(`Are you sure you want to delete the certificate for ${certificate.company_name}?`);
    setDeleteConfirmOpen(true);
  };

  const handleEditOrganization = (organization) => {
    setEditingOrganization(organization);
    setShowAddOrganization(true);
  };

  const handleDeleteOrganization = (organization) => {
    setOrganizationToDelete(organization);
    setDeleteConfirmText(`Are you sure you want to delete the organization ${organization.org_name || 'selected'}?`);
    setDeleteConfirmOpen(true);
  };

  // Close delete confirmation dialog
  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setCertificateToDelete(null);
    setDeleteConfirmText('');
    setDeleteConfirmInput('');
  };

  // Confirm and delete certificate or organization
  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      
      if (certificateToDelete) {
        await FirebaseService.deleteCertificate(certificateToDelete.id);
        
        // Remove from local state
        setCertificates(prevCerts => 
          prevCerts.filter(cert => cert.id !== certificateToDelete.id)
        );
        setFilteredCertificates(prevCerts => 
          prevCerts.filter(cert => cert.id !== certificateToDelete.id)
        );
      } else if (organizationToDelete) {
        await FirebaseService.deleteAccreditedOrganization(organizationToDelete.id);
        
        // Remove from local state
        setOrganizations(prevOrgs => 
          prevOrgs.filter(org => org.id !== organizationToDelete.id)
        );
        setFilteredOrganizations(prevOrgs => 
          prevOrgs.filter(org => org.id !== organizationToDelete.id)
        );
      } else if (personnelToDelete) {
        await FirebaseService.deleteVerifiedPersonnel(personnelToDelete.id);
        
        // Remove from local state
        setPersonnel(prevPersonnel => 
          prevPersonnel.filter(person => person.id !== personnelToDelete.id)
        );
        setFilteredPersonnel(prevPersonnel => 
          prevPersonnel.filter(person => person.id !== personnelToDelete.id)
        );
      }
      
      setDeleteConfirmOpen(false);
      setCertificateToDelete(null);
      setOrganizationToDelete(null);
      setPersonnelToDelete(null);
      setDeleteConfirmText('');
      setDeleteConfirmInput('');
      setLoading(false);
    } catch (error) {
      // Removed console.error
      setError('Failed to delete. Please try again.');
      setLoading(false);
    }
  };
  
  const toggleMobileDrawer = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Function to refresh certificates from server
  const refreshCertificates = async () => {
    try {
      setLoading(true);
      // Force refresh from server by passing true
      const data = await FirebaseService.getAllCertificates(true);
      
      // Sort certificates by issue_date in descending order (newest first)
      const sortedData = [...data].sort((a, b) => {
        // Parse dates properly handling different formats
        let dateA, dateB;
        
        try {
          // Handle various date formats (MMM D, YYYY or YYYY-MM-DD or D-MM-YYYY)
          if (a.issue_date) {
            if (a.issue_date.includes('-')) {
              // Handle YYYY-MM-DD or DD-MM-YYYY format
              const parts = a.issue_date.split('-');
              if (parts[0].length === 4) {
                // YYYY-MM-DD format
                dateA = new Date(a.issue_date);
              } else {
                // DD-MM-YYYY format (like 13-06-2023)
                dateA = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
              }
            } else {
              // Handle MMM D, YYYY format (like Dec 8, 2024)
              dateA = new Date(a.issue_date);
            }
          } else {
            dateA = new Date(0);
          }
          
          if (b.issue_date) {
            if (b.issue_date.includes('-')) {
              // Handle YYYY-MM-DD or DD-MM-YYYY format
              const parts = b.issue_date.split('-');
              if (parts[0].length === 4) {
                // YYYY-MM-DD format
                dateB = new Date(b.issue_date);
              } else {
                // DD-MM-YYYY format (like 13-06-2023)
                dateB = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
              }
            } else {
              // Handle MMM D, YYYY format (like Dec 8, 2024)
              dateB = new Date(b.issue_date);
            }
          } else {
            dateB = new Date(0);
          }
        } catch (error) {
          // Removed console.error
          return 0; // Keep original order if parsing fails
        }
        
        // Compare timestamps for sorting
        return dateB.getTime() - dateA.getTime(); // Descending order
      });
      
      setCertificates(sortedData);
      setFilteredCertificates(sortedData);
      // Removed console.log
    } catch (error) {
      // Removed console.error
      setError('Failed to refresh certificates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    // Now status is a boolean, true means active, false means expired
    if (status === true) {
      return 'success';
    } else if (status === false) {
      return 'error';
    } else {
      return 'default';
    }
  };

  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'N/A';
      
      // Handle different date formats
      let date;
      
      // Check if the date is in ISO format (from database)
      if (typeof dateString === 'string') {
        // Handle DD-MM-YYYY format (with dashes)
        if (dateString.includes('-') && dateString.split('-').length === 3) {
          const parts = dateString.split('-');
          // Check if it's DD-MM-YYYY or YYYY-MM-DD
          if (parts[0].length === 2) {
            // DD-MM-YYYY format
            date = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
          } else {
            // YYYY-MM-DD format
            date = new Date(dateString);
          }
        }
        // Handle DD/MM/YYYY format (with slashes)
        else if (dateString.includes('/')) {
          const [day, month, year] = dateString.split('/');
          date = new Date(`${year}-${month}-${day}`);
        }
        // Handle ISO format or other formats JS can parse
        else {
          date = new Date(dateString);
        }
      } else {
        date = new Date(dateString);
      }
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        // Removed console.log
        return dateString; // Return original if parsing failed
      }
      
      // Format the date consistently
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      // Removed console.error
      return dateString || 'N/A';
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        minHeight: '100vh'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: '#f5f5f5',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <Paper 
        elevation={1} 
        sx={{ 
          py: 1.5,
          px: 3,
          borderRadius: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#1976d2',
          color: 'white',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          position: 'relative'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isMobile && (
            <IconButton 
              color="inherit" 
              edge="start" 
              onClick={toggleMobileDrawer}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h5" component="h1" sx={{ fontWeight: 500 }}>
            {isMobile ? 'CMS' : 'Certificate Management System'}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title={user?.displayName || 'Admin'}>
            <IconButton color="inherit" sx={{ mr: 1 }}>
              <AccountCircleIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Logout">
            <IconButton 
              color="inherit" 
              onClick={handleLogout}
            >
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', flexGrow: 1 }}>
        {/* Desktop Sidebar */}
        {!isMobile && (
          <Paper 
            elevation={1} 
            sx={{ 
              width: 240, 
              borderRadius: 0,
              height: 'calc(100vh - 64px)',
              position: 'sticky',
              top: 64,
              zIndex: 1
            }}
          >
            <Tabs
              orientation="vertical"
              value={tabValue}
              onChange={handleTabChange}
              sx={{ 
                borderRight: 1, 
                borderColor: 'divider',
                '& .MuiTab-root': {
                  alignItems: 'flex-start',
                  textAlign: 'left',
                  pl: 2,
                  py: 1.5,
                  minHeight: '48px',
                  fontSize: '0.85rem'
                },
                '& .MuiSvgIcon-root': {
                  fontSize: '1.1rem',
                  mr: 0.5
                }
              }}
            >
              <Tab 
                icon={<DashboardIcon />} 
                iconPosition="start" 
                label="Dashboard" 
                sx={{ mb: 0.5 }}
              />
              <Tab 
                icon={<CertificateIcon />} 
                iconPosition="start" 
                label="Certificates" 
                sx={{ mb: 0.5 }}
              />
              <Tab 
                icon={<BusinessIcon />} 
                iconPosition="start" 
                label="Accredited Orgs" 
                sx={{ mb: 0.5 }}
              />
              <Tab 
                icon={<VerifiedUserIcon />} 
                iconPosition="start" 
                label="Verified Personnel" 
                sx={{ mb: 0.5 }}
              />
              <Tab 
                icon={<SettingsIcon />} 
                iconPosition="start" 
                label="Settings" 
                sx={{ mb: 0.5 }}
              />
            </Tabs>
          </Paper>
        )}
        
        {/* Mobile Drawer */}
        <Drawer
          variant={isMobile ? "temporary" : "permanent"}
          open={isMobile ? mobileDrawerOpen : true}
          onClose={toggleMobileDrawer}
          sx={{
            display: { xs: 'block', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 240,
              minWidth: 240,
              maxWidth: 240
            },
          }}
          PaperProps={{ sx: { width: 240, minWidth: 240, maxWidth: 240 } }}
        >
          <Box sx={{ 
            height: 56, // Smaller height for app bar
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#1976d2',
            color: 'white'
          }}>
            <Typography variant="subtitle1" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
              Menu
            </Typography>
          </Box>
          <List sx={{ py: 0.5 }}>
            <ListItem 
              component="div" 
              onClick={() => handleTabChange(null, 0)}
              sx={{ 
                py: 0.75, 
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                '& .MuiListItemIcon-root': { minWidth: '40px' },
                '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
                '& .MuiListItemText-primary': { fontSize: '0.85rem' }
              }}
            >
              <ListItemIcon>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText primary="Dashboard" />
            </ListItem>
            <ListItem 
              component="div" 
              onClick={() => handleTabChange(null, 1)}
              sx={{ 
                py: 0.75, 
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                '& .MuiListItemIcon-root': { minWidth: '40px' },
                '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
                '& .MuiListItemText-primary': { fontSize: '0.85rem' }
              }}
            >
              <ListItemIcon>
                <CertificateIcon />
              </ListItemIcon>
              <ListItemText primary="Certificates" />
            </ListItem>
            <ListItem 
              component="div" 
              onClick={() => handleTabChange(null, 2)}
              sx={{ 
                py: 0.75, 
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                '& .MuiListItemIcon-root': { minWidth: '40px' },
                '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
                '& .MuiListItemText-primary': { fontSize: '0.85rem' }
              }}
            >
              <ListItemIcon>
                <BusinessIcon />
              </ListItemIcon>
              <ListItemText primary="Accredited Orgs" />
            </ListItem>
            <ListItem 
              component="div" 
              onClick={() => handleTabChange(null, 3)}
              sx={{ 
                py: 0.75, 
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                '& .MuiListItemIcon-root': { minWidth: '40px' },
                '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
                '& .MuiListItemText-primary': { fontSize: '0.85rem' }
              }}
            >
              <ListItemIcon>
                <VerifiedUserIcon />
              </ListItemIcon>
              <ListItemText primary="Verified Personnel" />
            </ListItem>
            <ListItem 
              component="div" 
              onClick={() => handleTabChange(null, 4)}
              sx={{ 
                py: 0.75, 
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                '& .MuiListItemIcon-root': { minWidth: '40px' },
                '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
                '& .MuiListItemText-primary': { fontSize: '0.85rem' }
              }}
            >
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItem>
            <Divider sx={{ my: 0.5 }} />
            <ListItem 
              component="div" 
              onClick={handleLogout}
              sx={{ 
                py: 0.75, 
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                '& .MuiListItemIcon-root': { minWidth: '40px' },
                '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
                '& .MuiListItemText-primary': { fontSize: '0.85rem' }
              }}
            >
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItem>
          </List>
        </Drawer>

        {/* Main Content */}
        <Box
          component="main"
          sx={{ flexGrow: 1, p: { xs: 2, md: 4 }, ml: 0, width: { md: `calc(100% - 240px)` } }}
        >
          <Container maxWidth="xl" disableGutters>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
            
            {/* Dashboard Stats */}
            {tabValue === 0 && (
              <>
                <Typography variant="h4" sx={{ mb: 3 }}>
                  Dashboard
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
                  <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: '240px' }}>
                    <Card sx={{ bgcolor: '#e3f2fd', color: '#1976d2', height: '100%' }}>
                      <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="h4" sx={{ fontWeight: 500, fontSize: '1.5rem' }}>
                          {certificates.length}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                          Total Certificates
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                  
                  <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: '240px' }}>
                    <Card sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', height: '100%' }}>
                      <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="h4" sx={{ fontWeight: 500, fontSize: '1.5rem' }}>
                          {certificates.filter(cert => cert.status).length}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                          Active Certificates
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                  
                  <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: '240px' }}>
                    <Card sx={{ bgcolor: '#fff3e0', color: '#e65100', height: '100%' }}>
                      <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="h4" sx={{ fontWeight: 500, fontSize: '1.5rem' }}>
                          {certificates.filter(cert => !cert.status).length}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                          Expired Certificates
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                  
                  <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: '240px' }}>
                    <Card sx={{ bgcolor: '#e0f7fa', color: '#006064', height: '100%' }}>
                      <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="h4" sx={{ fontWeight: 500, fontSize: '1.5rem' }}>
                          {organizations.length}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                          Accredited Organizations
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                  
                  <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: '240px' }}>
                    <Card sx={{ bgcolor: '#f3e5f5', color: '#6a1b9a', height: '100%' }}>
                      <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="h4" sx={{ fontWeight: 500, fontSize: '1.5rem' }}>
                          {personnel.length}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                          Verified Personnel
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                  
                  <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: '240px' }}>
                    <Card sx={{ bgcolor: '#e8eaf6', color: '#3949ab', height: '100%' }}>
                      <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="h4" sx={{ fontWeight: 500, fontSize: '1.5rem' }}>
                          {new Set(certificates.map(cert => cert.company_name)).size}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                          Unique Companies
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                </Box>
                
                <Paper sx={{ p: 3, borderRadius: 2, mb: 4 }}>
                  <Typography variant="h5" sx={{ mb: 2 }}>
                    Recent Certificates
                  </Typography>
                  <TableContainer>
                    <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.8rem', padding: '6px 8px' } }}>
                      <TableHead>
                        <TableRow sx={{ '& .MuiTableCell-head': { fontWeight: 'bold', backgroundColor: '#1876D1', color: 'white', fontSize: '0.85rem' } }}>
                          <TableCell>Certificate No</TableCell>
                          <TableCell>Company Name</TableCell>
                          <TableCell>Issue Date</TableCell>
                          <TableCell>Expiry Date</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {certificates.slice(0, 5).map((cert) => (
                          <TableRow key={cert.id}>
                            <TableCell>{cert.certificate_no}</TableCell>
                            <TableCell>{cert.company_name}</TableCell>
                            <TableCell>{formatDate(cert.issue_date)}</TableCell>
                            <TableCell>{formatDate(cert.expiry_date)}</TableCell>
                            <TableCell>
                              <Chip 
                                label={cert.status === true ? 'ACTIVE' : 'INACTIVE'} 
                                color={getStatusColor(cert.status)}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {certificates.length > 5 && (
                    <Box sx={{ mt: 2, textAlign: 'right' }}>
                      <Button 
                        variant="text" 
                        color="primary"
                        onClick={() => setTabValue(1)}
                      >
                        View All Certificates
                      </Button>
                    </Box>
                  )}
                </Paper>
              </>
            )}
            
            {/* Certificates Management */}
            {tabValue === 1 && !showAddCertificate && (
              <>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3
                }}>
                  <Typography variant="h4">
                    Manage Certificates
                  </Typography>
                  
                  <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<AddCircleIcon />}
                    onClick={() => setShowAddCertificate(true)}
                  >
                    Add New Certificate
                  </Button>
                </Box>
                
                <Paper sx={{ p: 3, borderRadius: 2, mb: 4 }}>
                  <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
                    <TextField
                      fullWidth
                      placeholder="Search by certificate number or company name"
                      variant="outlined"
                      value={searchTerm}
                      onChange={handleSearch}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <IconButton onClick={refreshCertificates} color="primary" title="Refresh Certificates">
                      <RefreshIcon />
                    </IconButton>
                  </Box>
                  
                  <TableContainer>
                    <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.8rem', padding: '6px 8px' } }}>
                      <TableHead>
                        <TableRow sx={{ '& .MuiTableCell-head': { fontWeight: 'bold', backgroundColor: '#1876D1', color: 'white', fontSize: '0.85rem' } }}>
                          <TableCell>Certificate No</TableCell>
                          <TableCell>Company Name</TableCell>
                          <TableCell>Issue Date</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Expiry Date</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredCertificates.length > 0 ? (
                          filteredCertificates
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((cert) => (
                              <TableRow key={cert.id}>
                                <TableCell>{cert.certificate_no}</TableCell>
                                <TableCell>{cert.company_name}</TableCell>
                                <TableCell>{formatDate(cert.issue_date)}</TableCell>
                                <TableCell>{formatDate(cert.expiry_date)}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={cert.status === true ? 'ACTIVE' : 'INACTIVE'} 
                                    color={getStatusColor(cert.status)}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Tooltip title="Edit">
                                    <IconButton 
                                      size="small" 
                                      color="primary"
                                      onClick={() => handleEditCertificate(cert)}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton 
                                      size="small" 
                                      color="error"
                                      onClick={() => handleDeleteCertificate(cert)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))
                        ) : (
                          <TableRow sx={{ '& .MuiTableCell-head': { fontWeight: 'bold', backgroundColor: '#1876D1', color: 'white', fontSize: '0.85rem' } }}>
                            <TableCell colSpan={6} align="center">
                              {searchTerm ? 'No certificates match your search criteria' : 'No certificates found'}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredCertificates.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </Paper>
              </>
            )}
            
            {/* Add/Edit Certificate Form */}
            {tabValue === 1 && showAddCertificate && (
              <div style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e8ecf0',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                marginTop: '16px',
                overflow: 'hidden',
              }}>
                {/* Form Header */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '20px 28px', borderBottom: '1px solid #f1f5f9',
                  background: 'linear-gradient(135deg, #0a1628 0%, #0d2347 100%)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '8px',
                      background: 'rgba(201,162,39,0.2)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#c9a227" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                        <line x1="16" y1="13" x2="8" y2="13"/>
                        <line x1="16" y1="17" x2="8" y2="17"/>
                        <polyline points="10 9 9 9 8 9"/>
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', fontWeight: '600', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '2px' }}>
                        Certificates
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#ffffff' }}>
                        {editingCertificate ? 'Edit Certificate' : 'Add New Certificate'}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => { setShowAddCertificate(false); setEditingCertificate(null); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      height: '36px', padding: '0 16px',
                      background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '7px', color: '#ffffff', fontSize: '13px', fontWeight: '500',
                      cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
                    </svg>
                    Back to Certificates
                  </button>
                </div>

                {/* Form Body */}
                <div style={{ padding: '28px' }}>
                  <ReusableAddCertificateForm 
                    certificateToEdit={editingCertificate}
                    onCancel={() => {
                      setEditingCertificate(null);
                      setShowAddCertificate(false);
                    }}
                    onSuccess={() => {
                      setShowAddCertificate(false);
                      setEditingCertificate(null);
                      // Refresh certificates list after successful addition/update
                      setLoading(true); // Show loading indicator while fetching
                      FirebaseService.getAllCertificates().then(certs => {
                        setCertificates(certs);
                        setFilteredCertificates(certs);
                      }).catch(err => {
                        // Removed console.error
                        setError('Failed to refresh certificate list.');
                      }).finally(() => {
                        setLoading(false);
                      });
                    }}
                  />
                </div>
              </div>
            )}
            
            {/* Accredited Organizations */}
            {tabValue === 2 && !showAddOrganization && (
              <>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3
                }}>
                  <Typography variant="h4">
                    Manage Accredited Organizations
                  </Typography>
                  
                  <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<AddCircleIcon />}
                    onClick={() => {
                      setEditingOrganization(null);
                      setShowAddOrganization(true);
                    }}
                    sx={{ ml: 2 }}
                  >
                    Add Organization
                  </Button>
                </Box>
                
                <Paper sx={{ p: 3, borderRadius: 2, mb: 4 }}>
                  <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
                    <TextField
                      fullWidth
                      placeholder="Search by organization name or industry"
                      variant="outlined"
                      value={searchTerm}
                      onChange={handleSearch}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <IconButton onClick={refreshOrganizations} color="primary" title="Refresh Organizations">
                      <RefreshIcon />
                    </IconButton>
                  </Box>

                  {/* Organizations Table */}
                  <TableContainer>
                    <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.8rem', padding: '6px 8px' } }}>
                      <TableHead>
                        <TableRow sx={{ '& .MuiTableCell-head': { fontWeight: 'bold', backgroundColor: '#1876D1', color: 'white', fontSize: '0.85rem' } }}>
                          <TableCell>EXP. DATE</TableCell>
                          <TableCell>ORGANIZATION NAME</TableCell>
                          <TableCell>CERTIFICATE NO</TableCell>
                          <TableCell>CITY, STATE</TableCell>
                          <TableCell>COUNTRY</TableCell>
                          <TableCell>PROGRAM</TableCell>
                          <TableCell>STATUS</TableCell>
                          <TableCell align="center">ACTION</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredOrganizations.length > 0 ? (
                          filteredOrganizations
                            .sort((a, b) => {
                              // Handle N/A values
                              if (a.exp_date === 'N/A' && b.exp_date === 'N/A') return 0;
                              if (a.exp_date === 'N/A') return 1; // N/A values at the end
                              if (b.exp_date === 'N/A') return -1;
                              
                              // Parse dates and sort in descending order (newest first)
                              const dateA = new Date(a.exp_date);
                              const dateB = new Date(b.exp_date);
                              return dateB.getTime() - dateA.getTime();
                            })
                            .map((org) => (
                              <TableRow key={org.id}>
                                <TableCell>
                                  {!org.exp_date ? 'N/A' : (() => {
                                    try {
                                      const date = new Date(org.exp_date);
                                      const month = date.toLocaleString('en-US', { month: 'short' });
                                      const day = date.getDate();
                                      const year = date.getFullYear();
                                      return `${month} ${day}, ${year}`;
                                    } catch (e) {
                                      return org.exp_date;
                                    }
                                  })()}
                                </TableCell>
                                <TableCell>{org.org_name || 'N/A'}</TableCell>
                                <TableCell>{org.certi_no || 'N/A'}</TableCell>
                                <TableCell>
                                  {org.city ? (org.state ? `${org.city}, ${org.state}` : org.city) : 'N/A'}
                                </TableCell>
                                <TableCell>{org.country || 'N/A'}</TableCell>
                                <TableCell>{org.program || 'N/A'}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label="ACCREDITED" 
                                    color="success"
                                    size="small"
                                    sx={{ bgcolor: '#4caf50', color: 'white', fontWeight: 'bold', borderRadius: '16px', px: 1 }}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <IconButton 
                                    size="small" 
                                    color="primary" 
                                    onClick={() => {
                                      setEditingOrganization(org);
                                      setShowAddOrganization(true);
                                    }}
                                  >
                                    <EditIcon style={{ color: '#2196f3', fontSize: '1.2rem' }} />
                                  </IconButton>
                                  <IconButton 
                                    size="small" 
                                    color="error" 
                                    onClick={() => handleDeleteOrganization(org)}
                                  >
                                    <DeleteIcon style={{ color: '#f44336', fontSize: '1.2rem' }} />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={8} align="center">No organizations found</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </>
            )}
            
            {/* Add/Edit Organization Form */}
            {tabValue === 2 && showAddOrganization && (
              <div style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e8ecf0',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                marginTop: '16px',
                overflow: 'hidden',
              }}>
                {/* Form Header */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '20px 28px', borderBottom: '1px solid #f1f5f9',
                  background: 'linear-gradient(135deg, #0a1628 0%, #0d2347 100%)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '8px',
                      background: 'rgba(201,162,39,0.2)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#c9a227" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', fontWeight: '600', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '2px' }}>
                        Accredited Organizations
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#ffffff' }}>
                        {editingOrganization ? 'Edit Organization' : 'Add New Organization'}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => { setShowAddOrganization(false); setEditingOrganization(null); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      height: '36px', padding: '0 16px',
                      background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '7px', color: '#ffffff', fontSize: '13px', fontWeight: '500',
                      cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
                    </svg>
                    Back to Organizations
                  </button>
                </div>

                {/* Form Body */}
                <div style={{ padding: '28px' }}>
                <ReusableAddOrganizationForm 
                  organizationToEdit={editingOrganization}
                  onCancel={() => {
                    setEditingOrganization(null);
                    setShowAddOrganization(false);
                  }}
                  onSuccess={() => {
                    setShowAddOrganization(false);
                    setEditingOrganization(null);
                    // Refresh organizations list after successful addition/update
                    setLoading(true); // Show loading indicator while fetching
                    FirebaseService.getAllAccreditedOrganizations(true).then(orgs => {
                      setOrganizations(orgs);
                      setFilteredOrganizations(orgs);
                    }).catch(err => {
                      // Removed console.error
                      setError('Failed to refresh organization list.');
                    }).finally(() => {
                      setLoading(false);
                    });
                  }}
                />
                </div>
              </div>
            )}
            
            {/* Verified Personnel */}
            {tabValue === 3 && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5">
                    Manage Verified Personnel
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddCircleIcon />}
                    onClick={() => setShowAddPersonnel(true)}
                  >
                    Add New Personnel
                  </Button>
                </Box>
                
                <Paper sx={{ p: 3, borderRadius: 2, mb: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ position: 'relative', width: '100%', maxWidth: '500px' }}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        placeholder="Search by auditor name or certificate number"
                        value={searchTerm}
                        onChange={handleSearch}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                    <IconButton onClick={refreshPersonnel} color="primary" title="Refresh Personnel">
                      <RefreshIcon />
                    </IconButton>
                  </Box>

                  <TableContainer>
                    <Table sx={{ minWidth: 650 }}>
                      <TableHead sx={{ backgroundColor: '#1976d2' }}>
                        <TableRow>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>ISSUE DATE</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>AUDITOR NAME</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>CERTIFICATE NO</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>COUNTRY</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>STATUS</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>ACTION</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading && personnel.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                              <CircularProgress />
                            </TableCell>
                          </TableRow>
                        ) : personnel.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                              No verified personnel found.
                            </TableCell>
                          </TableRow>
                        ) : (
                          personnel
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((person) => (
                              <TableRow key={person.id} hover>
                                <TableCell>
                                  {formatDate(person.issue_date)}
                                </TableCell>
                                <TableCell>{person.auditor_name}</TableCell>
                                <TableCell>{person.certi_no}</TableCell>
                                <TableCell>{person.country || 'N/A'}</TableCell>
                                <TableCell>
                                  <Box
                                    sx={{
                                      display: 'inline-block',
                                      px: 2,
                                      py: 0.5,
                                      borderRadius: 1,
                                      backgroundColor: 
                                        person.status?.toUpperCase() === 'CERTIFIED' || person.status?.toUpperCase() === 'ACTIVE' || person.status?.toUpperCase() === 'ACCREDITED'
                                          ? '#4caf50' 
                                          : '#ff9800',
                                      color: 'white',
                                      fontWeight: 'bold',
                                      textTransform: 'uppercase',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    {person.status || 'N/A'}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <IconButton 
                                      color="primary" 
                                      size="small"
                                      onClick={() => {
                                        const personnelToEdit = personnel.find(p => p.id === person.id);
                                        if (personnelToEdit) {
                                          setEditingPersonnel(personnelToEdit);
                                          setShowAddPersonnel(true);
                                        }
                                      }}
                                      sx={{ color: '#2196f3' }}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                    <IconButton
                                      color="error"
                                      size="small"
                                      onClick={() => {
                                        setPersonnelToDelete(person);
                                        setDeleteConfirmOpen(true);
                                        setDeleteConfirmText(`Are you sure you want to delete the personnel "${person.auditor_name}"?`);
                                      }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={personnel.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </Paper>
              </>
            )}
            
            {/* Settings */}
            {tabValue === 4 && (
              <>
                <Typography variant="h4" sx={{ mb: 3 }}>
                  Settings
                </Typography>
                
                <Paper sx={{ p: 3, borderRadius: 2, mb: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    Account Information
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
                    <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: '240px' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '0.9rem' }}>
                        {user?.displayName || 'Admin User'}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: '240px' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        Email
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '0.9rem' }}>
                        {user?.email || 'admin@example.com'}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Divider sx={{ my: 3 }} />
                  
                  <Typography variant="h6" gutterBottom>
                    Security
                  </Typography>
                  
                  <Box sx={{ mt: 2 }}>
                    <Button variant="outlined" color="primary">
                      Change Password
                    </Button>
                  </Box>
                </Paper>
              </>
            )}
          </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description" sx={{ mb: 2 }}>
            {deleteConfirmText}
          </DialogContentText>
          <DialogContentText variant="subtitle2" color="error" sx={{ mb: 1 }}>
            Type "delete" to confirm:
          </DialogContentText>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            value={deleteConfirmInput}
            onChange={(e) => setDeleteConfirmInput(e.target.value)}
            placeholder="delete"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteConfirmOpen(false);
              setDeleteConfirmInput('');
            }} 
            color="primary"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error"
            disabled={deleteConfirmInput.toLowerCase() !== 'delete'}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Personnel Dialog */}
      <Dialog
        open={showAddPersonnel}
        onClose={() => {
          setShowAddPersonnel(false);
          setEditingPersonnel(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingPersonnel ? 'Edit Personnel' : 'Add New Personnel'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 1 }}>
            <AddVerifiedPersonnel 
              isDialog={true}
              personnelToEdit={editingPersonnel}
              onSuccess={() => {
                setShowAddPersonnel(false);
                setEditingPersonnel(null);
                // Refresh personnel list
                refreshPersonnel();
              }}
              onCancel={() => {
                setShowAddPersonnel(false);
                setEditingPersonnel(null);
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
      </Box>
    </Box>
  );
};


export default AdminDashboard;
